import React, { useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import {
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import Colors from "../../theme/colors.json";
import {
  CONFIRMATION_CONTENT,
  CONFIRMATION_TITLE,
  PREMIUM_BREAK_UP_SECTIONS,
  USER_GROUPS,
  handleValueRoundOff,
} from "../../constants/constants";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Fonts from "../../theme/font_size.json";
import { useSelector } from "react-redux";
import FileUpload from "../../components/FileUpload";

const useStyles = makeStyles((theme) => ({
  premiumBreakUptableEndorsement: {
    width: "100%",
    borderCollapse: "collapse",
    border: `1px solid ${Colors.BORDER}`,
    marginBottom: "20px",
    "& th, & td": {
      border: `1px solid ${Colors.BORDER}`,
      padding: "8px",
      textAlign: "left",
    },
    background: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
  },
  premiumBreakUptableHeadingEndorsement: {
    color: theme.palette.secondary.main,
    fontSize: theme.fontSizes.MEDIUM,
    fontWeight: 600,
  },
  validationContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableTextFieldStyles: {
    fontSize: theme.fontSizes.SMALL,
  },
  validationAction: {
    pointerEvents: "none",
  },
  endorsementDeleteBtnAction: {
    display: "flex",
    justifyContent: "flex-end",
  },
  endorsementContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
    // marginBottom: "2%",
    width: "49%",
  },
  endorsementLabel: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
  commonBtnStyles: {
    background: Colors.WHITE_BACKGROUND,
    textTransform: "capitalize",
  },
  premiumBreakUpTableHeadingCell: {
    fontSize: theme.fontSizes.SMALL,
    fontWeight: 600,
  },
  premiumBreakUpTableCell: {
    fontSize: theme.fontSizes.SMALL,
  },
  endorsementInput: {
    background: Colors.WHITE_BACKGROUND,
    fontSize: theme.fontSizes.SMALL,
    padding: "0.3rem",
  },
  endorsementPremiumDiv: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  endorsementDateInput: {
    background: Colors.WHITE_BACKGROUND,
    fontSize: theme.fontSizes.SMALL,
    padding: "0.3rem",
  },
  detailsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    margin: "2% 0 2% 0",
    rowGap: 20,
  },
}));

const PremiumBreakUpEndorsement = ({
  premiumDataArray,
  setPremiumDataArray,
  index,
  setEndorsementCommission,
}) => {
  const classes = useStyles();
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const handleYes = () => {
    setConfirmDialogOpen(false);

    setPremiumDataArray((prevState) => {
      const newArray = [...prevState]; // Make a copy of the previous state array
      newArray[index] = { ...prevState[index], validation: true }; // Update the specific index with new data
      return newArray; // Return the updated array
    });
  };

  const handleNo = () => {
    setConfirmDialogOpen(false);
  };
  const premiumSections = [
    {
      label: "Net Premium / OD (other than Motor TP, Terrorism & ERF)",
      section: PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM,
    },
    {
      label: "Motor TP Premium / Terrorism / Premium for ERF in case PL Act",
      section: PREMIUM_BREAK_UP_SECTIONS.MOTORTPPREMIUM,
    },
  ];

  const handleInputChange = async (e, section) => {
    const { name, value } = e.target;
    // const newValue = !isNaN(value) ? parseFloat(value) : value;
    const newValue = value;

    await setPremiumDataArray((prevData) => {
      const newArray = [...prevData];

      const sectionData = prevData[index][section];
      const netPremium = sectionData.netPremium;
      const GST = sectionData.gst;

      const gstPremium: any =
        (parseFloat(newValue) * (name === "netPremium" ? GST : netPremium)) /
        100;
      const premiumWithGst: any =
        name === "netPremium"
          ? parseFloat(newValue) + gstPremium
          : gstPremium + parseFloat(netPremium);

      const totalNetPremium =
        (name === "netPremium"
          ? parseFloat(newValue)
          : parseFloat(netPremium)) +
        (section === PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData[index]?.motorTpPremium?.netPremium)
          : parseFloat(prevData[index]?.netPremium?.netPremium));
      const totalGstPremium =
        parseFloat(gstPremium) +
        (section === PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData[index]?.motorTpPremium?.gstPremium)
          : parseFloat(prevData[index]?.netPremium?.gstPremium));
      const totalPremiumWithGst =
        parseFloat(premiumWithGst) +
        (section === PREMIUM_BREAK_UP_SECTIONS.NET_PREMIUM
          ? parseFloat(prevData[index]?.motorTpPremium?.premiumWithGst)
          : parseFloat(prevData[index]?.netPremium?.premiumWithGst));

      const updatedData = {
        ...prevData[index],
        [section]: {
          ...sectionData,
          [name]: newValue,
          gstPremium: gstPremium,
          premiumWithGst: premiumWithGst,
        },
        totalPremium: {
          totalNetPremium: handleValueRoundOff(totalNetPremium),
          totalGstPremium: handleValueRoundOff(totalGstPremium),
          totalPremiumWithGst: handleValueRoundOff(totalPremiumWithGst),
        },
        grossPremium: {
          grossPremiumWithGst: handleValueRoundOff(totalPremiumWithGst),
        },
      };

      newArray[index] = { ...updatedData };
      return newArray;
    });
  };

  const handleEndorsementsChange = (e) => {
    const { name, value } = e.target;

    setPremiumDataArray((prevState) => {
      const newArray = [...prevState];
      newArray[index] = {
        ...newArray[index],
        [name]: value,
      };
      return newArray;
    });
  };

  const handleDelete = (indexToDelete) => {
    setPremiumDataArray((prevData) => {
      // Ensure the index is valid
      if (indexToDelete >= 0) {
        // Use filter to exclude the row with the specified index
        return prevData.filter((_, index) => index !== indexToDelete);
      } else {
        // If the index is invalid, return the previous data
        return prevData;
      }
    });

    setEndorsementCommission((prevData) => {
      // Ensure the index is valid
      if (indexToDelete >= 0) {
        // Use filter to exclude the row with the specified index
        return prevData.filter((_, index) => index !== indexToDelete);
      } else {
        // If the index is invalid, return the previous data
        return prevData;
      }
    });
  };

  const handleFile = async (file) => {
    await setPremiumDataArray((prevState) => {
      const newArray = [...prevState]; // Make a copy of the previous state array
      newArray[index] = { ...prevState[index], endorsementDocument: file }; // Update the specific index with new data
      return newArray; // Return the updated array
    });
    await setEndorsementCommission((prevState) => {
      const newArray = [...prevState];
      newArray[index] = {
        ...prevState[index],
        endorsementDocument: file,
      };
      return newArray;
    });
  };

  useEffect(() => {
    setEndorsementCommission((prevData) => {
      const newArray = [...prevData];

      newArray[index] = {
        ...prevData[index],
        netPremium: {
          ...prevData[index]?.netPremium,
          netPremium: premiumDataArray[index]?.netPremium?.netPremium,
          commissionAmount:
            (premiumDataArray[index]?.netPremium?.netPremium *
              prevData[index]?.netPremium?.commission) /
            100,
          rewardAmount:
            (premiumDataArray[index]?.netPremium?.netPremium *
              prevData[index]?.netPremium?.reward) /
            100,
        },
        motorTpPremium: {
          ...prevData[index]?.motorTpPremium,
          netPremium: premiumDataArray[index]?.motorTpPremium?.netPremium,
          commissionAmount:
            (premiumDataArray[index]?.motorTpPremium?.netPremium *
              prevData[index]?.motorTpPremium?.commission) /
            100,
          rewardAmount:
            (premiumDataArray[index]?.motorTpPremium?.netPremium *
              prevData[index]?.motorTpPremium?.reward) /
            100,
        },
        totalPremium: {
          ...prevData[index]?.totalPremium,
          totalNetPremium:
            premiumDataArray[index]?.netPremium?.netPremium +
            premiumDataArray[index]?.motorTpPremium?.netPremium,
        },
        endorsementEffectiveDate:
          premiumDataArray[index]?.endorsementEffectiveDate,
      };
      return newArray;
    });
  }, [
    premiumDataArray[index]?.netPremium?.netPremium,
    premiumDataArray[index]?.motorTpPremium?.netPremium,
    premiumDataArray[index]?.endorsementEffectiveDate,
  ]);

  return (
    <div className={classes.endorsementPremiumDiv}>
      <div
        className={
          premiumDataArray[index]?.validation ? classes.validationAction : ""
        }
      >
        <div className={classes.detailsContainer}>
          <div className={classes.endorsementContainer}>
            <label className={classes.endorsementLabel}>
              Endorsement Number {index + 1}
            </label>
            <TextField
              variant="standard"
              className={classes.endorsementInput}
              id="EndorsementNumber"
              name="endorsementNumber"
              onChange={handleEndorsementsChange}
              value={premiumDataArray[index]?.endorsementNumber}
              InputProps={{
                disableUnderline: true,
                classes: {
                  input: classes.endorsementInput,
                },
              }}
            />
          </div>
          <div className={classes.endorsementContainer}>
            <label className={classes.endorsementLabel}>
              Endorsement Effective date {index + 1}
            </label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD/MM/YYYY"
                value={
                  premiumDataArray[index]?.endorsementEffectiveDate || null
                }
                onChange={(newValue) =>
                  handleEndorsementsChange({
                    target: {
                      value: newValue,
                      name: "endorsementEffectiveDate",
                    },
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    inputProps={{
                      ...params.inputProps,
                      placeholder: "DD/MM/YYYY",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      className: classes.endorsementDateInput,
                    }}
                    size="small"
                  />
                )}
              />
            </LocalizationProvider>
          </div>
          <div className={classes.endorsementContainer}>
            <label className={classes.endorsementLabel}>
              Reason For Endorsement {index + 1}
            </label>
            <TextField
              variant="standard"
              className={classes.endorsementInput}
              id="ReasonForEndorsementChange"
              name="reasonForEndorsement"
              multiline
              rows={3}
              onChange={handleEndorsementsChange}
              value={premiumDataArray[index]?.reasonForEndorsement}
              InputProps={{
                disableUnderline: true,
                classes: {
                  input: classes.endorsementInput,
                },
              }}
            />
          </div>
          <div className={classes.endorsementContainer}>
            {/* <label className={classes.endorsementLabel}>
            Endorsement Document {index + 1}
          </label> */}

            <FileUpload
              title={`Endorsement Document ${index + 1}`}
              handleFile={handleFile}
              previousFile={premiumDataArray[index]?.endorsementDocument}
            />
          </div>
        </div>
        <ConfirmationDialog
          open={confirmDialogOpen}
          setOpen={setConfirmDialogOpen}
          title={CONFIRMATION_TITLE}
          content={CONFIRMATION_CONTENT}
          handleYes={handleYes}
          handleNo={handleNo}
        />
        <table className={classes.premiumBreakUptableEndorsement}>
          <thead>
            <tr>
              <th
                colSpan={4}
                className={classes.premiumBreakUptableHeadingEndorsement}
              >
                Endorsement Premium {index + 1}
              </th>
              <th className={classes.premiumBreakUptableHeadingEndorsement}>
                <div className={classes.endorsementDeleteBtnAction}>
                  {index >= 0 ? (
                    <IconButton onClick={() => handleDelete(index)}>
                      <Tooltip title="Delete">
                        <DeleteIcon color="secondary" />
                      </Tooltip>
                    </IconButton>
                  ) : null}
                </div>
              </th>
            </tr>
            <tr>
              <th></th>
              <th className={classes.premiumBreakUpTableHeadingCell}>
                Net Premium
              </th>
              <th className={classes.premiumBreakUpTableHeadingCell}>GST %</th>
              <th className={classes.premiumBreakUpTableHeadingCell}>
                GST Premium
              </th>
              <th className={classes.premiumBreakUpTableHeadingCell}>
                Premium with GST
              </th>
            </tr>
          </thead>
          <tbody>
            {premiumSections?.map((section) => (
              <tr key={section?.label}>
                <td className={classes.premiumBreakUpTableCell}>
                  {section?.label}
                </td>

                {Object.keys(premiumDataArray[index][section?.section])?.map(
                  (field) => (
                    <td key={field}>
                      <TextField
                        type={"number"}
                        name={field}
                        size="small"
                        disabled={
                          field === "gstPremium" || field === "premiumWithGst"
                            ? true
                            : false
                        }
                        value={premiumDataArray[index][section?.section][field]}
                        onChange={(e) => handleInputChange(e, section?.section)}
                        InputProps={{
                          ...(field === "gst" && {
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          }),
                          ...(field !== "gst" && {
                            startAdornment: (
                              <InputAdornment position="start">
                                ₹
                              </InputAdornment>
                            ),
                          }),
                          className: classes.tableTextFieldStyles,
                        }}
                      />
                    </td>
                  )
                )}
              </tr>
            ))}

            <tr>
              <td className={classes.premiumBreakUpTableCell}>TOTAL PREMIUM</td>

              <td>
                <TextField
                  type="number"
                  name="netPremium"
                  size="small"
                  disabled
                  value={premiumDataArray[index]?.totalPremium?.totalNetPremium}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
              <td>
                {/* <TextField
                type="number"
                name="gst"
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
              /> */}
              </td>
              <td>
                <TextField
                  type="number"
                  name="gstPremium"
                  size="small"
                  disabled
                  value={premiumDataArray[index]?.totalPremium?.totalGstPremium}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
              <td>
                <TextField
                  type="number"
                  name="premiumWithGst"
                  size="small"
                  disabled
                  value={
                    premiumDataArray[index]?.totalPremium?.totalPremiumWithGst
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} className={classes.premiumBreakUpTableCell}>
                GROSS PREMIUM
              </td>
              <td>
                <TextField
                  type="number"
                  disabled
                  size="small"
                  value={
                    premiumDataArray[index]?.grossPremium?.grossPremiumWithGst
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                    className: classes.tableTextFieldStyles,
                  }}
                />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className={classes.validationContainer}>
        {userGroup === USER_GROUPS.ADMIN ? (
          <Button
            className={classes.commonBtnStyles}
            onClick={() =>
              setPremiumDataArray((prevState) => {
                const newArray = [...prevState]; // Make a copy of the previous state array
                newArray[index] = { ...prevState[index], validation: false }; // Update the specific index with new data
                return newArray; // Return the updated array
              })
            }
            variant="outlined"
            size="small"
            disabled={
              premiumDataArray[index]?.validation === true ? false : true
            }
          >
            Revalidation
          </Button>
        ) : (
          <div></div>
        )}
        <Button
          className={classes.commonBtnStyles}
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => setConfirmDialogOpen(true)}
          disabled={premiumDataArray[index]?.validation === true ? true : false}
        >
          Validation
        </Button>
      </div>
    </div>
  );
};

export default PremiumBreakUpEndorsement;
